/* eslint-disable */

import Vue from "vue";
import ShardsVue from "shards-vue";
import * as Sentry from "@sentry/vue";
import VueCookies from "vue-cookies";
import VueSessionStorage from "vue-sessionstorage";
import Vuelidate from "vuelidate";
import VueToast from "vue-toast-notification";
import VueSimpleAlert from "vue-simple-alert";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import advancedFormat from "dayjs/plugin/advancedFormat";
import utc from "dayjs/plugin/utc";

// Styles
import "bootstrap/dist/css/bootstrap.css";
import "@/scss/shards-dashboards.scss";
import "@/assets/scss/date-range.scss";
import "bootstrap-vue/dist/bootstrap-vue.css";
import "vue-toast-notification/dist/theme-sugar.css";

// Core
import App from "./App.vue";
import Default from "@/layouts/Default.vue";
import StatusBadge from "@/components/StatusBadge.vue";
import CalendarIcon from "@/components/icons/CalendarIcon.vue";
import store from "./store";
import router from "./router";
import i18n from "./i18n";
import http, { api, fetchIpAddress, setRouter } from "@/services/http";
import { installLookupCache } from "@/utils/lookupCache";

setRouter(router);
installLookupCache(store);

// Safety net for the global loader: every SPA navigation clears any loader
// count left behind by a request that errored before its matching
// changeShowLoader(false). Without this, one leaked counter keeps the overlay
// (and any E2E loader wait) stuck indefinitely on the next page.
router.afterEach(() => {
  store.commit('resetLoader');
});

import Mixin from "@/utils/mixin";
import keepAliveRefresh from "@/mixins/keepAliveRefresh";
import { formatFullName, formatSmartDate } from "@/utils/formatters";
import TEST_IDS from "@/constants/testIds";
import "@/utils/directive";

// Plugins
Vue.use(VueCookies);
Vue.use(VueSessionStorage);
Vue.use(Vuelidate);
Vue.use(VueToast, { duration: 6000 });
Vue.use(VueSimpleAlert);
ShardsVue.install(Vue);

// Dayjs
dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);
Object.defineProperties(Vue.prototype, {
  $date: {
    get() {
      return dayjs;
    },
  },
});

// Global registrations
Vue.config.productionTip = false;
Vue.component("default-layout", Default);
Vue.component("StatusBadge", StatusBadge);
Vue.component("CalendarIcon", CalendarIcon);
Vue.prototype.$axios = http;
Vue.prototype.$api = api;
Vue.prototype.$eventHub = new Vue();
Vue.prototype.$fullName = formatFullName;
Vue.prototype.$testIds = TEST_IDS;
// Run async work with the global loader on, guaranteeing the loader is turned
// off afterwards even if the work throws. Prefer this over a bare
// commit(true)/commit(false) pair, which leaks the loader count on error paths.
Vue.prototype.$withLoader = async function (work) {
  this.$store.commit('changeShowLoader', true);
  try {
    return await work();
  } finally {
    this.$store.commit('changeShowLoader', false);
  }
};
Vue.prototype.$smartDate = function (datetime) {
  return formatSmartDate(
    datetime,
    this.formatDate,
    this.$t('generalMessages.today'),
    this.$t('generalMessages.yesterday'),
  );
};
Vue.mixin(Mixin);
Vue.mixin(keepAliveRefresh);

// Filters
Vue.filter("camelize", (value) => {
  if (!value) return "";
  const str = value.toString();
  return str.charAt(0).toUpperCase() + str.slice(1);
});

Vue.filter("capitalize", (value) => {
  if (!value) return "";
  return value.toString().toUpperCase();
});

// Directives
Vue.directive("decimal-amount", {
  bind(el, binding, vnode) {
    el.addEventListener("input", (event) => {
      let value = event.target.value;

      value = value.replace(/[^0-9.]/g, "");

      const parts = value.split(".");
      if (parts.length > 2) {
        value = parts[0] + "." + parts[1];
      }

      event.target.value = value;

      if (vnode.data.model && vnode.data.model.callback) {
        vnode.data.model.callback(value);
      }
    });
  }
});

// Sentry
if (process.env.VUE_APP_SENTRY_DSN) {
  Sentry.init({
    Vue,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
    ],
    environment: process.env.NODE_ENV,
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.2 : 1.0,
  });
}

// Init
fetchIpAddress();

new Vue({
  router,
  store,
  i18n,
  render: (h) => h(App),
}).$mount("#app");
